import { MapPin, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { StatusBadge } from "./status-badge";
import type { WorkflowStatus } from "@/lib/design-tokens";

interface SiteMetric {
  label: string;
  value: string | number;
}

interface SiteAlert {
  message: string;
  severity: "warning" | "danger";
}

interface SiteCommandCenterProps {
  siteName: string;
  siteCode: string;
  address?: string;
  status: WorkflowStatus;
  metrics: SiteMetric[];
  alerts?: SiteAlert[];
  className?: string;
}

export function SiteCommandCenter({
  siteName,
  siteCode,
  address,
  status,
  metrics,
  alerts = [],
  className,
}: SiteCommandCenterProps) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border border-navy-deep/10 bg-navy-deep text-white shadow-[var(--shadow-elevated)]",
        className,
      )}
    >
      <div className="blueprint-bg-subtle relative px-5 py-4">
        <div
          className="absolute inset-0 bg-navy-deep/95"
          aria-hidden
        />
        <div className="relative flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-orange-construction" />
              <span className="font-mono text-xs uppercase tracking-widest text-orange-construction">
                {siteCode}
              </span>
            </div>
            <h2 className="mt-1 text-xl font-bold">{siteName}</h2>
            {address ? (
              <p className="mt-0.5 text-sm text-white/70">{address}</p>
            ) : null}
          </div>
          <StatusBadge status={status} className="shrink-0" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-px bg-navy-blueprint/50 sm:grid-cols-4">
        {metrics.map((metric) => (
          <div key={metric.label} className="bg-navy-blueprint px-4 py-3">
            <p className="text-xs uppercase tracking-wider text-white/60">{metric.label}</p>
            <p className="mt-0.5 text-lg font-bold tabular-nums">{metric.value}</p>
          </div>
        ))}
      </div>

      {alerts.length > 0 ? (
        <div className="space-y-2 border-t border-white/10 bg-navy-deep/80 px-5 py-3">
          {alerts.map((alert) => (
            <div
              key={alert.message}
              className={cn(
                "flex items-center gap-2 text-sm",
                alert.severity === "danger" ? "text-red-300" : "text-amber-300",
              )}
            >
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {alert.message}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function SiteCommandCenterDemo() {
  return (
    <SiteCommandCenter
      siteName="Ridgepoint Tower — Phase 2"
      siteCode="SITE-042"
      address="1847 Industrial Blvd, Austin TX"
      status="Submitted"
      metrics={[
        { label: "Crew on site", value: "18" },
        { label: "Hours today", value: "142.5" },
        { label: "Equipment", value: "6" },
        { label: "Exceptions", value: "2" },
      ]}
      alerts={[
        { message: "2 time entries missing foreman sign-off", severity: "warning" },
      ]}
    />
  );
}
